/**
 * Pure school-selection rules (no DOM / zustand) so node tests can import
 * them. Persistence lives in schoolSelection.ts (zustand persist, same
 * pattern as CRM leads `user-preferences`).
 */

export interface SchoolOption {
  id: string;
  name: string;
  orgId?: string;
}

export interface ResolvedSchoolContext {
  schoolId: string | undefined;
  selectedSchool: SchoolOption | undefined;
  isWildcardScope: boolean;
  schools: SchoolOption[];
  canSwitch: boolean;
}

export function parsePersistedSelection(raw: string | null): SchoolOption | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'id' in parsed && typeof (parsed as SchoolOption).id === 'string') {
      const option = parsed as { id: string; name?: unknown; orgId?: unknown };
      const result: SchoolOption = {
        id: option.id,
        name: typeof option.name === 'string' ? option.name : '',
      };
      if (typeof option.orgId === 'string') result.orgId = option.orgId;
      return result;
    }
  } catch {
    // legacy value was a bare school id
  }
  if (raw.length > 0 && raw[0] !== '{' && raw[0] !== '[') {
    return { id: raw, name: '' };
  }
  return null;
}

function inScope(options: SchoolOption[], filterIds?: string[]): SchoolOption[] {
  if (!filterIds) return options;
  return options.filter((school) => filterIds.includes(school.id));
}

export function resolveSchoolContext(input: {
  schoolIds: string[] | undefined;
  orgId?: string;
  fetchedSchools: SchoolOption[];
  cachedSchools: SchoolOption[];
  persisted: SchoolOption | null;
}): ResolvedSchoolContext {
  const isWildcardScope = input.schoolIds?.includes('*') ?? false;
  const concreteIds = isWildcardScope ? [] : (input.schoolIds ?? []);
  const scopeFilter = isWildcardScope ? undefined : concreteIds;

  const fetched = inScope(input.fetchedSchools, scopeFilter);
  const cached = inScope(input.cachedSchools, scopeFilter);
  let schools = fetched.length > 0 ? fetched : cached;

  const persistedOrgOk = !input.persisted?.orgId || !input.orgId || input.persisted.orgId === input.orgId;
  const persistedInClaim = Boolean(
    input.persisted &&
      persistedOrgOk &&
      (isWildcardScope || concreteIds.includes(input.persisted.id)),
  );
  const persistedInList = Boolean(
    input.persisted && schools.some((school) => school.id === input.persisted?.id),
  );
  // School-level staff: always their one campus, never a leftover org pick.
  const singleSchoolId = !isWildcardScope && concreteIds.length === 1 ? concreteIds[0] : undefined;
  const persistedIsValid =
    !singleSchoolId &&
    persistedInClaim &&
    (schools.length === 0 || persistedInList || fetched.length === 0);

  const schoolId =
    singleSchoolId ??
    (persistedIsValid ? input.persisted!.id : (schools[0]?.id ?? (!isWildcardScope ? concreteIds[0] : undefined)));

  const fromList = schoolId ? schools.find((school) => school.id === schoolId) : undefined;
  const selectedSchool: SchoolOption | undefined = fromList
    ?? (input.persisted && input.persisted.id === schoolId ? input.persisted : undefined)
    ?? (schoolId ? { id: schoolId, name: '' } : undefined);

  if (selectedSchool?.name && !schools.some((school) => school.id === selectedSchool.id)) {
    schools = [selectedSchool, ...schools];
  }

  return {
    schoolId,
    selectedSchool,
    isWildcardScope,
    schools,
    canSwitch: isWildcardScope || concreteIds.length > 1,
  };
}
